import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from 'next-themes';
import { ThemeToggle } from '@/components/ThemeToggle';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import { NotificationHistory } from '@/components/NotificationHistory';
import { AccountMenu } from '@/components/AccountMenu';
import { UpgradeBanner } from '@/components/enforcement/UpgradeBanner';
import { UsageIndicator } from '@/components/enforcement/UsageIndicator';
import { PageErrorBoundary } from '@/components/PageErrorBoundary';
import { PageTransition } from '@/components/PageTransition';
import { ContextJourneyBand } from '@/components/context/ContextJourneyBand';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { KeyboardShortcutsDialog } from '@/components/KeyboardShortcutsDialog';
import { useLanguage } from '@/hooks/useLanguage';
import { useConversionMilestones } from '@/hooks/useConversionMilestones';
import { useCatalogSyncedMilestone } from '@/hooks/useCatalogSyncedMilestone';
import { captureEntryMarker } from '@/lib/entryMarker';
import { AccessRevokedBanner } from '@/components/AccessRevokedBanner';
import { AccessRevokedOverlay } from '@/components/AccessRevokedOverlay';
import { B2BConfirmHost } from '@/components/checkout/B2BConfirmDialog';
import { GoogleAuthRevokedBanner } from '@/components/GoogleAuthRevokedBanner';
import { B2B_NOTICE_ROUTES } from '@/lib/b2bNotice';
import { APP_TITLE, documentTitleFor } from '@/lib/documentTitle';
import { readSidebarDefaultOpen } from '@/lib/sidebarState';
import logoLight from '@/assets/logo-light.svg';
import logoDark from '@/assets/logo-dark.svg';
import icon from '@/assets/icon.svg';
import iconDark from '@/assets/icon-dark.svg';

/**
 * Die Entwurfsroute, als Konstante statt als Literal-String.
 *
 * Traegt seit R4 (Entlastung Rahmen) nur noch FLUID_ROUTES -- der
 * B2B-Hinweis haengt seitdem an der eigenen, geteilten Liste
 * `B2B_NOTICE_ROUTES` (`src/lib/b2bNotice.ts`) und kennt diese Konstante
 * nicht mehr.
 */
const ENTWURF_ROUTE = '/internal/entwurf-werkbank';
const FLUID_ROUTES = ['/workbench', ENTWURF_ROUTE];

// Routes that should use full available width (no max-width container)
// `/internal/entwurf-werkbank` rendert dieselbe Werkbank hinter
// Schaltern und braucht deshalb dieselbe Hoehenkette. Fehlt sie hier,
// loest das `h-full` der Seite seine 100 % gegen `height: auto` auf und
// die Karte waechst ueber den sichtbaren Bereich hinaus - der Entwurf
// zeigte dann ein Layout, das es so nie gibt.

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  // Herkunftsmarker der Landung (`?entry=web`) fangen, BEVOR
  // useConversionMilestones ihn liest. Effekte laufen in der Reihenfolge, in
  // der ihre Hooks aufgerufen werden, deshalb steht dieser hier oben. Der
  // Aufruf neben captureFlowIntent im FeedHealthDashboard wuerde nicht
  // reichen: der Marker muss auch dann gefangen werden, wenn die Landung auf
  // einer anderen Route endet.
  useEffect(() => {
    captureEntryMarker();
  }, []);
  useLanguage();
  // Genau EIN Montagepunkt fuer die Konversions-Meilensteine, siehe
  // Kopfkommentar des Hooks. Layout ist der einzige Rahmen aller geschuetzten
  // Routen.
  useConversionMilestones();
  // Zweiter Meilenstein (catalog_synced), gleicher Montagepunkt aus demselben Grund.
  useCatalogSyncedMilestone();
  const { t } = useTranslation('common');
  const { user, signOut } = useAuth();
  const { resolvedTheme } = useTheme();
  const { showShortcuts, setShowShortcuts } = useKeyboardShortcuts();
  const location = useLocation();
  const isFluid = FLUID_ROUTES.includes(location.pathname);

  // A3-21: ein Titel je Seite statt "FeedOptimizer.AI" auf allen Routen.
  useEffect(() => {
    document.title = documentTitleFor(location.pathname, t);
  }, [location.pathname, t]);
  // Routen ausserhalb des Rahmens (/auth, /auth/google/callback, NotFound,
  // /case-study-consent) setzen keinen Titel. Beim Verlassen von Layout auf
  // den App-Namen aus index.html zurueck, sonst stuende auf der 404-Karte
  // oder nach dem Abmelden der Titel der zuletzt besuchten Seite.
  useEffect(() => () => {
    document.title = APP_TITLE;
  }, []);

  /**
   * R4 (Entlastung Rahmen, docs/superpowers/specs/2026-09-07-app-
   * entlastung-design.md, Abschnitt 5.1): der Hinweis sichert den KAUF ab,
   * nicht den Betrieb. Bis hierher rendert diese Zeile unter JEDER Seite,
   * die durch `Layout` laeuft -- auch unter dem Feed-Check und der
   * Werkbank, wo nie ein Preis faellt. Seit R4 haengt er an der geteilten
   * Routenliste `B2B_NOTICE_ROUTES` (`src/lib/b2bNotice.ts`, dort auch die
   * vierte Stelle des Hinweises, das Checkout-Gate `B2BConfirmDialog`) statt
   * an einem Ausschluss ueber `ENTWURF_ROUTE`.
   *
   * `/settings` traegt seine Reiter als Query-Parameter (`?tab=accounts`),
   * `location.pathname` bleibt dabei exakt `/settings` -- ein einfacher
   * Mitgliedschaftsvergleich reicht also fuer alle Settings-Tabs.
   *
   * Damit ist die fruehere offene Anwaltsfrage (BGH I ZR 60/16, "klarer
   * Hinweis auf jeder Seite", docs/anwalt-anfrage-agb-preisumbau.md:39)
   * bewusst zugunsten von "nur auf den Kaufwegen" entschieden -- der Hinweis
   * steht seitdem nirgends mehr auf `/workbench` oder der Entwurfsroute,
   * das war vorher offen und ist es jetzt nicht mehr. `ENTWURF_ROUTE` bleibt
   * als Konstante bestehen, sie traegt weiterhin `FLUID_ROUTES`.
   */
  const zeigeB2BHinweis = B2B_NOTICE_ROUTES.includes(
    location.pathname as (typeof B2B_NOTICE_ROUTES)[number],
  );

  return (
    <SidebarProvider defaultOpen={readSidebarDefaultOpen(typeof document === 'undefined' ? '' : document.cookie)}>
      {/* A3-21: erster Tab-Stopp. Oben im Dokument, damit `.sr-only`
          (position: absolute) hier keinen Scroll erzeugt (siehe Kommentar
          am <main>). */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-[60] focus:rounded-md focus:bg-background focus:px-4 focus:py-3 focus:text-sm focus:font-medium focus:shadow-lg focus:ring-2 focus:ring-primary"
      >
        {t('skipToContent')}
      </a>
      <div className="h-screen flex w-full bg-background overflow-hidden max-w-[100vw] print:h-auto print:overflow-visible">
        <div className="print:hidden">
          <AppSidebar />
        </div>

        <div className="flex-1 flex flex-col min-w-0">
          {/*
            One sticky container for the whole banner stack. Both banners used to carry
            `sticky top-0 z-40` themselves, so as soon as both were active they pinned to
            the same offset and the lower one disappeared under the upper one on scroll.
            In here they stay in normal flow and stack.
          */}
          <div className="sticky top-0 z-40">
            {/* Google-Auth-Banner: whole OAuth connection revoked (account level). */}
            <GoogleAuthRevokedBanner />
            {/* GMC-Access-Banner: persistent warning when the active MC lost access. */}
            <AccessRevokedBanner />
          </div>
          {/*
            Header und Kontextband sind hier zu EINER 56-px-Leiste
            zusammengefuehrt (Spec docs/superpowers/specs/2026-09-07-app-
            entlastung-design.md, Abschnitt 5.1, R1). Vorher standen zwei
            Leisten uebereinander (dieser `h-14`-Header plus ein eigenes
            `ContextJourneyBand` mit eigenem border-b/bg-background/py-1.5,
            zusammen rund 130 px). Border, Hintergrund, Polsterung und
            `print:hidden` traegt jetzt ausschliesslich diese Leiste;
            `ContextJourneyBand` ist nur noch eine Zeile darin (`min-w-0
            flex-1`), kein eigenstaendiges Band mehr.
          */}
          <header className="h-14 border-b border-border flex items-center px-2 sm:px-4 gap-1.5 sm:gap-3 bg-background min-w-0 print:hidden">
            {/*
              `shrink-0` auf allen vier direkten Header-Kindern ausser dem
              Band (Trigger, beide Logo-Links, rechte Knopfgruppe): Produktions-
              Messung 07./08.09.2026 zeigte, dass ohne diese Sicherung nicht
              nur die Kontext-Pille schrumpft, sondern JEDES Flex-Kind mit
              Default-`flex-shrink:1` -- inklusive der rechten Knopfgruppe, die
              dadurch selbst zusammengestaucht werden konnte und dann die
              gemessene Ueberdeckung von Kapazitaetsring/Glocke erklaert
              (Ziel 2: nichts darf die rechte Gruppe verdecken). Einzig
              `ContextJourneyBand` (das Band mit der Kontext-Pille) darf
              weiterhin schrumpfen -- es ist der `flex-1`-Wachser dieser
              Leiste und traegt selbst die Reihenfolge des Weglassens
              (Pille zuerst schuetzen, Journey zuerst weichen lassen, siehe
              `FeedSwitcher.tsx`/`ContextJourneyBand.tsx`).
            */}
            <SidebarTrigger className="h-11 w-11 shrink-0 md:hidden" />
            {/*
              Bruchstelle `md` (768px) statt `xs` (480px, Rebrand-Stand):
              Produktions-Messung 07./08.09.2026 zeigte bei 480px einen
              NICHT-monotonen Einbruch der Kontext-Pille (375px: 44,9px ->
              480px: 23,9px, obwohl das Fenster breiter wurde) -- Ursache war
              genau dieser Wechsel vom kleinen Mark (24px) auf das volle
              Lockup (deutlich breiter), der der Pille mitten in einem sonst
              wachsenden Fenster ploetzlich Platz wegnahm. Der Weg zur
              Startseite geht unterhalb `md` weiterhin ueber den kleinen Mark
              UND ueber die Sidebar (Eintrag "Feed Check" fuehrt auf "/",
              `AppSidebar.tsx`) -- kein Verlust der Navigation, nur ein
              spaeterer Umstieg auf das breite Lockup, an einer Breite (768px),
              bei der laut Messung ohnehin genug Platz fuer alles da ist.
            */}
            <Link to="/" aria-label="FeedOptimizer.AI: Feed Check" className="hidden md:block shrink-0">
              <img
                src={resolvedTheme === 'dark' ? logoDark : logoLight}
                alt="FeedOptimizer.AI"
                className="h-5 sm:h-7 w-auto"
              />
            </Link>
            {/*
              Im Dark Mode steht die Petrol-Kachel des Marks auf bg-background (#0B2A2C):
              Kontrast 2,02:1, unter der 3:1-Grenze fuer grafische Objekte. Die inverse
              Fassung (Sand-Kachel, Petrol-Ebenen) traegt sich dort stattdessen.
            */}
            {/*
              Rang 5 der Reihenfolge des Weglassens (die Marke selbst):
              unter `xs` (480 px) faellt auch der kleine Mark weg. Die
              Bilanz der Leiste bei 320 px laesst nichts anderes zu (volle
              Tabelle im Kopf von `context/FeedSwitcher.tsx`): Rand 16,
              Ausloeser 32, Abstaende 12 und die rechte Knopfgruppe 168
              lassen 92 px fuer die Kontext-Pille uebrig, und die braucht
              sie fuer einen lesbaren Feednamen (54 px gemessen). Mit dem
              Mark waeren es 62 px Pille und damit wieder ein Feedname
              knapp ueber der Unkenntlichkeit. Der Weg zur Startseite geht
              dort ueber die Sidebar (Eintrag "Feed Check" fuehrt auf "/",
              `AppSidebar.tsx:39`), der Ausloeser dafuer steht direkt
              daneben.
            */}
            <Link to="/" aria-label="FeedOptimizer.AI: Feed Check" className="hidden xs:block md:hidden shrink-0">
              <img
                src={resolvedTheme === 'dark' ? iconDark : icon}
                alt="FeedOptimizer.AI"
                className="h-6 w-6"
              />
            </Link>

            <ContextJourneyBand />

            {/*
              `ml-auto` haelt diese Gruppe (Kapazitaetsring, Glocke,
              Theme-Umschalter, Kontomenue) unabhaengig davon rechtsbuendig,
              ob `ContextJourneyBand` ueberhaupt rendert. Das Band ist der
              einzige `flex-1`-Wachser dieser Leiste, gibt aber `null`
              zurueck, solange kein Merchant Center gewaehlt ist (Erstkontakt)
              -- ohne `ml-auto` klebte die Gruppe dann direkt am Logo statt am
              rechten Rand (Final-Review-Fund F1). `shrink-0` (Produktions-
              Messung 07./08.09.2026, Ziel 2 dieses Fixes): ohne sie durfte
              der Flexbox-Algorithmus auch DIESE Gruppe zusammendruecken,
              sobald die Leiste insgesamt zu schmal wurde -- ihre Kinder sind
              Icon-Knoepfe ohne eigene Truncate-Logik, ein Zusammendruecken
              hiess dort Ueberlappen statt Schrumpfen.
            */}
            <div className="flex items-center gap-1 sm:gap-2 min-w-0 shrink-0 ml-auto">
              <UsageIndicator />
              <NotificationHistory />
              {/* Unter sm im Kontomenue (K30, Platzbilanz in FeedSwitcher.tsx). */}
              <div data-testid="header-theme-toggle" className="hidden sm:block">
                <ThemeToggle />
              </div>
              <AccountMenu
                email={user?.email}
                onShowShortcuts={() => setShowShortcuts(true)}
                onSignOut={signOut}
              />
            </div>
          </header>

          {/* Main content */}
          {/* `relative` ist hier kein Layout-Wunsch, sondern ein Bezugsrahmen.
              `overflow-y-auto` macht diesen Knoten zum Scroll-Container, aber
              NICHT zum umschliessenden Block absolut positionierter Nachfahren
              — das tut allein `position`. Tailwinds `.sr-only` ist
              `position: absolute`; ohne positionierten Vorfahren loest so eine
              Spanne gegen den Initial Containing Block auf, entkommt damit der
              Klammer dieses Containers und verlaengert das DOKUMENT bis zu ihrer
              statischen Position. Auf dem Feed-Check erzeugte allein die
              sr-only-Zusammenfassung der Sparkline so einen ZWEITEN Scrollbalken
              (Produktion 09.09.2026, 1440x900: `documentElement.scrollHeight`
              1048 statt 900 px, mit geoeffnetem Preis-Kapitel 1343 px) — und
              weil dann das Dokument mitscrollt, waehrend die App-Huelle auf
              `h-screen` steht, schob sich der Inhalt unter den sichtbaren
              Bereich. `relative` gibt den 25 `sr-only`-Verwendungen der App in
              18 Dateien ihren Bezug INNERHALB des Scrollers zurueck. Ueber
              sechs Routen gemessen wechselt dadurch kein einziger SICHTBARER
              Knoten seinen Bezugsrahmen — betroffen sind ausschliesslich
              1x1-px-sr-only-Spannen (`/` 1, `/workbench` 10, die uebrigen
              vier 0).

              Es ist der ZWEITE Fall derselben Mechanik binnen zwei Tagen:
              am 08.09.2026 entkam die sr-only-Spanne der Kategoriezeile ihrem
              `truncate` und dem `overflow-x-hidden` dieses `<main>` und
              erzeugte bei 1024 px einen HORIZONTALEN Seiten-Scroll
              (`documentElement.scrollWidth` 1052 gegen 1024). Behoben wurde
              das damals lokal mit `relative` an genau jenem Knopf (siehe den
              ausfuehrlichen Kommentar in `workbench/card/AttributeChips.tsx`)
              — der Knoten blieb also stehen. Deshalb sitzt der Fix jetzt am
              Scroll-Container statt am Fundort: die Falle schnappt bei jeder
              der 25 Stellen zu, sobald eine davon weit genug unten oder weit
              genug rechts landet.

              `pb-24` (96 px) statt `py-4`: der schwebende Feedback-Knopf
              belegt unten rechts 24 bis 72 px (bottom-6, h-12). Ohne
              Reserve lag die letzte Aktion einer Seite mobil darunter
              (K07, 360x780: Knopf y636-684, CTA y638-682). Die Werkbank
              (isFluid) blendet den Knopf aus und braucht die Reserve nicht. */}
          <main id="main-content" tabIndex={-1} className={`relative flex-1 overflow-y-auto overflow-x-hidden min-w-0 ${isFluid ? 'flex flex-col' : 'px-4 sm:px-8 lg:px-12 pt-4 sm:pt-6 pb-24'}`}>
            <div className={isFluid ? 'flex-1 flex flex-col min-h-0 w-full' : 'max-w-6xl mx-auto'}>
              <UpgradeBanner inset={isFluid} />
              <PageErrorBoundary>
                {/* Der Bewegungs-Container muss die Hoehenbegrenzung
                    weiterreichen, sonst loest das `h-full` der Fluid-Seite
                    seine 100 % gegen `height: auto` auf und waechst ueber den
                    sichtbaren Bereich hinaus. Ausfuehrliche Begruendung in
                    PageTransition.tsx.

                    Erst ab `lg` (1024 px, dieselbe Schwelle wie
                    NARROW_VIEWPORT_BREAKPOINT): darunter ist ueber der Karte
                    so viel Seite, dass die Begrenzung ihr nichts mehr uebrig
                    laesst - gemessen 1 px Karte auf einem 781 px hohen
                    Fenster. Schmal darf die Seite deshalb weiter wachsen und
                    `<main>` scrollen. */}
                <PageTransition
                  className={isFluid ? 'flex flex-col lg:min-h-0 lg:flex-1' : undefined}
                >
                  {children}
                </PageTransition>
              </PageErrorBoundary>
            </div>
            {/* B2B-Ausschluss (Paket B Umsatzsteuer): dezenter Hinweis unter
                dem Seiteninhalt, scrollt mit (kein Overlay, nicht fixed). */}
            {zeigeB2BHinweis && (
              <p className="mt-8 pb-4 text-center text-xs text-muted-foreground">
                {t('b2bNotice')}
              </p>
            )}
          </main>
        </div>
      </div>
      <KeyboardShortcutsDialog open={showShortcuts} onOpenChange={setShowShortcuts} />
      {/* Blocking modal — only renders when selected MC is permission_denied + not yet dismissed/overridden. */}
      <AccessRevokedOverlay />
      {/* B2B-Bestaetigungs-Gate vor dem Stripe-Checkout: genau EIN Host, alle
          handleUpgrade-Aufrufer liegen innerhalb von Layout-Routen. */}
      <B2BConfirmHost />
    </SidebarProvider>
  );
};
export default Layout;